import React, { useState, useEffect, useRef } from 'react';
import WorkspaceHeader from './WorkspaceHeader';
import FilterNav from './FilterNav';
import WorkspaceList from './WorkspaceList';
import MultiSelect from '../ui/MultiSelect';
import Toast from '../ui/Toast';
import SidePanel from '../ui/SidePanel';
import colors from '../../utils/colors';

const Workspace = ({ isCreating, newSongs, onCoverSong, onKaraokeSong, onExtendSong, onReplaceSection, onReuseLyrics, onIntroSong, onSpeedSong, onPlaySong, onPauseSong, currentlyPlayingSong, hasPlaybar = false, onSidePanelStateChange, onCollapseNavigation }) => {
  const [activeFilters, setActiveFilters] = useState(['hide-disliked', 'hide-stems']);
  const [searchValue, setSearchValue] = useState('');
  const [filterCount, setFilterCount] = useState(2);
  const [currentSort, setCurrentSort] = useState('newest');
  const [currentView, setCurrentView] = useState('list');
  const [likedSongs, setLikedSongs] = useState(new Set());
  const [dislikedSongs, setDislikedSongs] = useState(new Set());
  const [publishedSongs, setPublishedSongs] = useState(new Set());
  const [selectedSongs, setSelectedSongs] = useState(new Set());
  const [deletedSongs, setDeletedSongs] = useState(new Set());
  const [playedSongs, setPlayedSongs] = useState(new Set());
  const [showToast, setShowToast] = useState(false);
  const [deletedSongsBackup, setDeletedSongsBackup] = useState(new Set());
  const [scrollOpacity, setScrollOpacity] = useState(0);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [sidePanelSong, setSidePanelSong] = useState(null);
  const [songLikeCounts, setSongLikeCounts] = useState(new Map());
  const [songTitles, setSongTitles] = useState(new Map());
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreSongs, setHasMoreSongs] = useState(true);
  const [additionalSongs, setAdditionalSongs] = useState([]);
  const [loadedPages, setLoadedPages] = useState(1); // Track how many pages have been loaded
  const contentRef = useRef(null);

  const handleFilterClick = (filterId) => {
    setActiveFilters(prev => {
      const isActive = prev.includes(filterId);
      let newFilters;
      
      if (isActive) {
        // Remove filter
        newFilters = prev.filter(id => id !== filterId);
      } else {
        // Add filter
        newFilters = [...prev, filterId];
      }
      
      // Update filter count (excluding 'filters' button itself)
      setFilterCount(newFilters.filter(id => id !== 'filters').length);
      
      return newFilters;
    });
    
    console.log('Filter clicked:', filterId);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    console.log('Search changed:', value);
  };

  const handleSortChange = (sortId) => {
    setCurrentSort(sortId);
    console.log('Sort changed:', sortId);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const handleTitleEdit = (songId, newTitle) => {
    setSongTitles(prev => {
      const newTitles = new Map(prev);
      newTitles.set(songId, newTitle);
      return newTitles;
    });
  };

  const handleCellAction = (action, songId, songData) => {
    console.log(`Cell action: ${action} for song ${songId}`);
    
    // Handle specific actions
    switch (action) {
      case 'publish':
        setPublishedSongs(prev => {
          const newPublished = new Set(prev);
          if (newPublished.has(songId)) {
            newPublished.delete(songId);
          } else {
            newPublished.add(songId);
          }
          return newPublished;
        });
        console.log(`Publishing song ${songId}`);
        break;
      case 'remix':
        console.log(`Remixing song ${songId}`);
        break;
      case 'cover':
        console.log(`Creating cover for song ${songId}`);
        onCoverSong?.(songData);
        break;
      case 'karaoke':
        console.log(`Creating karaoke for song ${songId}`);
        onKaraokeSong?.(songData);
        break;
      case 'extend':
        console.log(`Extending song ${songId}`);
        onExtendSong?.(songData);
        break;
      case 'replaceSection':
        console.log(`Replace section for song ${songId}`);
        onReplaceSection?.(songData);
        break;
      case 'reuseLyrics':
        console.log(`Reuse lyrics for song ${songId}`);
        onReuseLyrics?.(songData);
        break;
      case 'intro':
        console.log(`Creating intro for song ${songId}`);
        onIntroSong?.(songData);
        break;
      case 'speed':
        console.log(`Creating speed version for song ${songId}`);
        onSpeedSong?.(songData);
        break;
      case 'like':
        const wasLiked = likedSongs.has(songId);
        setLikedSongs(prev => {
          const newLiked = new Set(prev);
          if (wasLiked) {
            newLiked.delete(songId);
          } else {
            newLiked.add(songId);
          }
          return newLiked;
        });
        
        // Update like count
        setSongLikeCounts(prev => {
          const newCounts = new Map(prev);
          const currentAdjustment = newCounts.get(songId) || 0;
          if (wasLiked) {
            newCounts.set(songId, currentAdjustment - 1);
          } else {
            newCounts.set(songId, currentAdjustment + 1);
          }
          return newCounts;
        });
        
        console.log(`Liked song ${songId}`);
        break;
      case 'dislike':
        const wasDisliked = dislikedSongs.has(songId);
        setDislikedSongs(prev => {
          const newDisliked = new Set(prev);
          if (wasDisliked) {
            newDisliked.delete(songId);
          } else {
            newDisliked.add(songId);
            // Remove from liked if it was liked
            setLikedSongs(prevLiked => {
              const newLiked = new Set(prevLiked);
              newLiked.delete(songId);
              return newLiked;
            });
          }
          return newDisliked;
        });
        console.log(`${wasDisliked ? 'Undisliked' : 'Disliked'} song ${songId}`);
        break;
      case 'share':
        console.log(`Sharing song ${songId}`);
        break;
      case 'menu':
        console.log(`Opening menu for song ${songId}`);
        break;
      case 'play':
        console.log(`Playing song ${songId}`);
        // Find the song and open side panel when playing
        const allSongs = [...(newSongs || []), ...getWorkspaceSongs(), ...additionalSongs];
        const songToShow = allSongs.find(song => song.id === songId);
        if (songToShow) {
          setSidePanelSong(songToShow);
          setSidePanelOpen(true);
          onSidePanelStateChange?.(true, 'workspace');
        }
        break;
      case 'select':
        setSelectedSongs(prev => {
          const newSelected = new Set(prev);
          if (newSelected.has(songId)) {
            newSelected.delete(songId);
          } else {
            newSelected.add(songId);
          }
          return newSelected;
        });
        console.log(`Selected song ${songId}`);
        break;
      default:
        console.log(`Unknown action: ${action}`);
    }
  };

  const handleWorkspaceHeaderAction = (action) => {
    console.log(`Workspace header action: ${action}`);
  };

  // MultiSelect handlers
  const handleMultiSelectClear = () => {
    setSelectedSongs(new Set());
  };

  const handleMultiSelectDownload = () => {
    console.log('Downloading selected songs:', Array.from(selectedSongs));
    // Implement download logic here
  };

  const handleMultiSelectNewWorkspace = () => {
    console.log('Creating new workspace with selected songs:', Array.from(selectedSongs));
    // Implement new workspace logic here
  };

  const handleMultiSelectAddToPlaylist = () => {
    console.log('Adding selected songs to playlist:', Array.from(selectedSongs));
    // Implement add to playlist logic here
  };

  const handleMultiSelectDelete = () => {
    const selectedSongIds = Array.from(selectedSongs);
    console.log('Deleting selected songs:', selectedSongIds);
    
    // Store the deleted songs for undo functionality
    setDeletedSongsBackup(new Set(selectedSongIds));
    
    // Mark songs as deleted
    setDeletedSongs(prev => {
      const newDeleted = new Set(prev);
      selectedSongIds.forEach(id => newDeleted.add(id));
      return newDeleted;
    });
    
    // Clear selection
    setSelectedSongs(new Set());
    
    // Show toast
    setShowToast(true);
  };

  const handleUndo = () => {
    // Restore deleted songs
    setDeletedSongs(prev => {
      const newDeleted = new Set(prev);
      deletedSongsBackup.forEach(id => newDeleted.delete(id));
      return newDeleted;
    });
    
    // Clear backup and hide toast
    setDeletedSongsBackup(new Set());
    setShowToast(false);
  };

  const handleToastDismiss = () => {
    setShowToast(false);
    // Keep songs deleted after toast dismissal
    setDeletedSongsBackup(new Set());
  };

  // Generate additional songs for infinite scroll
  const generateAdditionalSongs = (startId, count = 10) => {
    const songTitles = [
      "Midnight Echoes", "Digital Sunrise", "Velvet Thunder", "Cosmic Drift", 
      "Urban Legends", "Neon Dreams", "Starlight Serenade", "Electric Storm",
      "Ocean Waves", "Mountain High", "Forest Whispers", "Desert Wind",
      "Arctic Glow", "Tropical Vibes", "City Nights", "Country Roads",
      "Jazz Lounge", "Rock Anthem", "Pop Sensation", "Classical Beauty",
      "Electronic Pulse", "Ambient Flow", "Punk Energy", "Soul Revival",
      "Reggae Rhythm", "Blues Highway", "Folk Tale", "Metal Thunder"
    ];
    
    const genres = [
      "Electronic, Synthwave, Ambient",
      "Rock, Alternative, Indie",
      "Jazz, Smooth Jazz, Lounge",
      "Pop, Dance, Electronic",
      "Hip Hop, R&B, Urban",
      "Classical, Orchestral, Cinematic",
      "Folk, Acoustic, Country",
      "Metal, Hard Rock, Progressive",
      "Reggae, Dub, World Music",
      "Blues, Soul, Funk"
    ];
    
    const images = [
      "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=67&h=67&fit=crop&crop=center",
      "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=67&h=67&fit=crop&crop=center",
      "https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=67&h=67&fit=crop&crop=center",
      "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
      "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
      "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
      "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
      "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=67&h=67&fit=crop&crop=center",
      "https://images.unsplash.com/photo-1619983081563-430f63602796?w=67&h=67&fit=crop&crop=center",
      "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
      "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=67&h=67&fit=crop&crop=center"
    ];

    const durations = ["2:34", "3:21", "4:15", "2:58", "5:43", "3:07", "4:32", "2:45", "6:18", "3:56"];
    const statuses = ["active", "inactive", "pending"];

    return Array.from({ length: count }, (_, i) => {
      const id = startId + i;
      return {
        id,
        title: songTitles[i % songTitles.length],
        genres: genres[i % genres.length],
        duration: durations[i % durations.length],
        image: images[i % images.length],
        status: statuses[i % statuses.length],
        isNew: false,
        likeCount: Math.floor(Math.random() * 5000) + 100,
        commentCount: Math.floor(Math.random() * 500) + 20
      };
    });
  };

  const handleLoadMore = () => {
    if (isLoadingMore || !hasMoreSongs) return;
    
    setIsLoadingMore(true);
    
    // Simulate loading more songs with actual new content
    setTimeout(() => {
      const currentTotal = getUpdatedNewSongs().length + getWorkspaceSongsWithCounts().length + additionalSongs.length;
      const startId = 1000 + additionalSongs.length; // Use high IDs to avoid conflicts
      const newSongs = generateAdditionalSongs(startId, 10);
      
      setAdditionalSongs(prev => [...prev, ...newSongs]);
      setLoadedPages(prev => prev + 1); // Increment page count when new songs are loaded
      
      // Stop loading after we reach around 80 total songs
      if (currentTotal + newSongs.length > 80) {
        setHasMoreSongs(false);
      }
      
      setIsLoadingMore(false);
    }, 1500);
  };

  const handleSidePanelClose = () => {
    setSidePanelOpen(false);
    setSidePanelSong(null);
    onSidePanelStateChange?.(false, 'workspace');
  };

  // Helper function to get current like count for a song
  const getCurrentLikeCount = (songId) => {
    const allSongs = [...(newSongs || []), ...getWorkspaceSongs(), ...additionalSongs];
    const song = allSongs.find(s => s.id === songId);
    const originalCount = song?.likeCount || 0;
    const adjustment = songLikeCounts.get(songId) || 0;
    return originalCount + adjustment;
  };

  const handleSidePanelLike = () => {
    if (sidePanelSong) {
      const wasLiked = likedSongs.has(sidePanelSong.id);
      const countChange = wasLiked ? -1 : 1;
      
      setLikedSongs(prev => {
        const newLiked = new Set(prev);
        if (wasLiked) {
          newLiked.delete(sidePanelSong.id);
        } else {
          newLiked.add(sidePanelSong.id);
        }
        return newLiked;
      });

      // Update like count
      setSongLikeCounts(prev => {
        const newCounts = new Map(prev);
        const currentAdjustment = newCounts.get(sidePanelSong.id) || 0;
        newCounts.set(sidePanelSong.id, currentAdjustment + countChange);
        return newCounts;
      });

      // Update the sidePanelSong object to reflect new count
      setSidePanelSong(prev => ({
        ...prev,
        likeCount: prev.likeCount + countChange
      }));
    }
  };

  const handleRowClick = (songId) => {
    console.log(`Row clicked for song ${songId}`);
    // Find the song and open side panel
    const allSongs = [...(newSongs || []), ...getWorkspaceSongs(), ...additionalSongs];
    const songToShow = allSongs.find(song => song.id === songId);
    if (songToShow) {
      setSidePanelSong(songToShow);
      setSidePanelOpen(true);
      onSidePanelStateChange?.(true, 'workspace');
    }
  };

  // Get workspace songs data with updated like counts and titles
  const getWorkspaceSongsWithCounts = () => {
    const baseSongs = getWorkspaceSongs();
    const baseSongsWithUpdates = baseSongs.map(song => ({
      ...song,
      title: songTitles.get(song.id) || song.title,
      likeCount: getCurrentLikeCount(song.id)
    }));
    
    // Add additional loaded songs with updates
    const additionalSongsWithUpdates = additionalSongs.map(song => ({
      ...song,
      title: songTitles.get(song.id) || song.title,
      likeCount: getCurrentLikeCount(song.id)
    }));
    
    return [...baseSongsWithUpdates, ...additionalSongsWithUpdates];
  };

  // Apply title updates to newSongs as well
  const getUpdatedNewSongs = () => {
    return newSongs.map(song => ({
      ...song,
      title: songTitles.get(song.id) || song.title,
      likeCount: getCurrentLikeCount(song.id)
    }));
  };

  // Get workspace songs data (same as WorkspaceList)
  const getWorkspaceSongs = () => {
    return [
      {
        id: 1,
        title: "Blue Light Goodbye",
        genres: "Techno, House, Minimal Techno",
        duration: "2:56",
        image: "https://images.unsplash.com/photo-1611348586804-61bf6c080437?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1200,
        commentCount: 230
      },
      {
        id: 2,
        title: "Lost in Translation (remix)",
        genres: "Nordic Folk, Pagan Metal, Dark Ambient",
        duration: "3:42",
        image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=67&h=67&fit=crop&crop=center",
        status: "inactive",
        isNew: false,
        likeCount: 890,
        commentCount: 156
      },
      {
        id: 3,
        title: "Electric Pulse",
        genres: "EDM, Progressive House, Trance",
        duration: "4:18",
        image: "https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2100,
        commentCount: 342
      },
      {
        id: 4,
        title: "After Hours",
        genres: "Jazz, Smooth Jazz, Neo-Soul",
        duration: "5:23",
        image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=67&h=67&fit=crop&crop=center",
        status: "pending",
        isNew: false,
        likeCount: 567,
        commentCount: 89
      },
      {
        id: 5,
        title: "Untitled",
        genres: "Ambient, Chillout, New Age",
        duration: "6:12",
        image: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 3400,
        commentCount: 467
      },
      {
        id: 6,
        title: "Neon Nights",
        genres: "Synthwave, Retrowave, Cyberpunk",
        duration: "3:57",
        image: "https://images.unsplash.com/photo-1619983081563-430f63602796?w=67&h=67&fit=crop&crop=center",
        status: "inactive",
        isNew: false,
        likeCount: 1890,
        commentCount: 278
      },
      {
        id: 7,
        title: "Campfire Stories",
        genres: "Folk, Acoustic, Nature Sounds",
        duration: "4:31",
        image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 745,
        commentCount: 123
      },
      {
        id: 8,
        title: "Binary Dreams (remix)",
        genres: "Drum & Bass, Neurofunk, Electronic",
        duration: "3:14",
        image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=67&h=67&fit=crop&crop=center",
        status: "pending",
        isNew: false,
        likeCount: 1567,
        commentCount: 234
      },
      {
        id: 9,
        title: "Interstellar",
        genres: "Space Rock, Psychedelic, Progressive",
        duration: "7:45",
        image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 4200,
        commentCount: 445
      },
      {
        id: 10,
        title: "City Lights",
        genres: "Hip Hop, R&B, Soul",
        duration: "3:28",
        image: "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=67&h=67&fit=crop&crop=center",
        status: "inactive",
        isNew: false,
        likeCount: 923,
        commentCount: 167
      },
      {
        id: 11,
        title: "Untitled",
        genres: "Country, Bluegrass, Folk Rock",
        duration: "4:02",
        image: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 678,
        commentCount: 98
      },
      {
        id: 12,
        title: "Frequency Shift (remix)",
        genres: "Glitch Hop, Future Bass, Experimental",
        duration: "5:17",
        image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
        status: "pending",
        isNew: false,
        likeCount: 1345,
        commentCount: 201
      },
      {
        id: 13,
        title: "Solar Flare",
        genres: "Rock, Alternative Rock, Indie",
        duration: "3:36",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 2890,
        commentCount: 389
      },
      {
        id: 14,
        title: "Deep Waters",
        genres: "Meditation, Ambient, Healing",
        duration: "8:21",
        image: "https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=67&h=67&fit=crop&crop=center",
        status: "inactive",
        isNew: false,
        likeCount: 512,
        commentCount: 67
      },
      {
        id: 15,
        title: "Nostalgic",
        genres: "Lo-Fi, Chillhop, Downtempo",
        duration: "4:44",
        image: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=67&h=67&fit=crop&crop=center",
        status: "active",
        isNew: false,
        likeCount: 1789,
        commentCount: 289
      }
    ];
  };

  // Keep sidePanelSong in sync with updated like counts and titles
  useEffect(() => {
    if (sidePanelSong) {
      // Check both newSongs and workspace songs for updates
      const updatedNewSongs = getUpdatedNewSongs();
      const updatedWorkspaceSongs = getWorkspaceSongsWithCounts();
      const allUpdatedSongs = [...updatedNewSongs, ...updatedWorkspaceSongs];
      
      const updatedSong = allUpdatedSongs.find(song => song.id === sidePanelSong.id);
      if (updatedSong && (updatedSong.likeCount !== sidePanelSong.likeCount || updatedSong.title !== sidePanelSong.title)) {
        setSidePanelSong(updatedSong);
      }
    }
  }, [songLikeCounts, songTitles, sidePanelSong?.id, newSongs, additionalSongs]);

  // Handle scroll for gradient effect and infinite loading
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        
        // Handle gradient effect
        const opacity = Math.min(scrollTop / 100, 1);
        setScrollOpacity(opacity);
        
        // Handle infinite scroll - trigger load more when near the end
        const isNearEnd = scrollTop + clientHeight >= scrollHeight - 200; // 200px buffer
        if (isNearEnd && hasMoreSongs && !isLoadingMore) {
          handleLoadMore();
        }
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [hasMoreSongs, isLoadingMore]);

  return (
    <div className="flex flex-col h-full bg-transparent flex-1 w-full relative border-l border-white/10">
      {/* Content Area - Scrollable (behind headers) */}
      <div 
        ref={contentRef}
        className="absolute inset-0 overflow-y-auto"
        style={{
          right: sidePanelOpen ? '274px' : '0' // Make room for side panel (250px + 24px margin)
        }}
      >
        <div 
          className="px-4 pt-[140px]"
          style={{ paddingBottom: hasPlaybar ? '100px' : '20px' }}
        > {/* Add top padding for fixed headers and bottom padding for playbar */}
          <WorkspaceList 
            onCellAction={handleCellAction} 
            isCreating={isCreating} 
            isCompact={currentView === 'compact'}
            newSongs={getUpdatedNewSongs()}
            activeFilters={activeFilters}
            likedSongs={likedSongs}
            dislikedSongs={dislikedSongs}
            publishedSongs={publishedSongs}
            selectedSongs={selectedSongs}
            deletedSongs={deletedSongs}
            playedSongs={playedSongs}
            searchValue={searchValue}
            multiSelectMode={selectedSongs.size > 0}
            onPlaySong={onPlaySong}
            onPauseSong={onPauseSong}
            currentlyPlayingSong={currentlyPlayingSong}
            onMarkAsPlayed={(songId) => setPlayedSongs(prev => new Set([...prev, songId]))}
            onRowClick={handleRowClick}
            songs={getWorkspaceSongsWithCounts()}
            onCollapseNavigation={onCollapseNavigation}
            isLoadingMore={isLoadingMore}
            hasMoreSongs={hasMoreSongs}
            additionalSongs={additionalSongs}
            loadedPages={loadedPages}
          />
        </div>
      </div>

      {/* Sticky Header Container - WorkspaceHeader + FilterNav */}
      <div 
        className="sticky top-0 z-20 bg-transparent pointer-events-auto"
        style={{
          left: '0px',
          right: sidePanelOpen ? '274px' : '0px'
        }}
      >
        {/* Workspace Header */}
        <div 
          style={{
            paddingTop: '12px',
            paddingLeft: '16px', 
            paddingRight: sidePanelOpen ? '16px' : '16px',
            paddingBottom: '12px',
            height: '80px' // 56px content + 24px padding
          }}
        >
          <WorkspaceHeader
            onBack={() => handleWorkspaceHeaderAction('back')}
            onEdit={() => handleWorkspaceHeaderAction('edit')}
            onAddAudio={() => handleWorkspaceHeaderAction('addAudio')}
            onShare={() => handleWorkspaceHeaderAction('share')}
            isCollapsed={sidePanelOpen}
          />
        </div>

        {/* Filter Navigation */}
        <div>
          <FilterNav
            activeFilters={activeFilters}
            searchValue={searchValue}
            filterCount={filterCount}
            currentSort={currentSort}
            onFilterClick={handleFilterClick}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            sidePanelOpen={sidePanelOpen}
            hideStyles={true}
            currentView={currentView}
            onViewChange={handleViewChange}
          />
        </div>
      </div>

      {/* Gradient Overlay - creates soft transition, fades in on scroll */}
      <div 
        className="absolute top-0 left-0 right-0 z-10 pointer-events-none"
        style={{
          opacity: scrollOpacity,
          height: '180px'
        }}
      >
        <div
          style={{
            marginRight: sidePanelOpen ? '274px' : '0',
            background: `linear-gradient(180deg, ${colors.bg.primary} 0%, ${colors.bg.primary} 40%, ${colors.bg.primary}F2 60%, ${colors.bg.primary}D9 75%, ${colors.bg.primary}99 90%, ${colors.bg.primary}00 100%)`,
            height: '100%'
          }}
        />
      </div>

      {/* MultiSelect component - anchored to bottom, centered within workspace */}
      {!showToast && (
        <div 
          className="absolute left-4 z-50 flex justify-center pointer-events-none"
          style={{ 
            bottom: hasPlaybar ? '108px' : '24px',
            right: sidePanelOpen ? '290px' : '16px' // Adjust for side panel
          }}
        >
          <div className="pointer-events-auto">
            <MultiSelect
              selectedCount={selectedSongs.size}
              onClear={handleMultiSelectClear}
              onDownload={handleMultiSelectDownload}
              onNewWorkspace={handleMultiSelectNewWorkspace}
              onAddToPlaylist={handleMultiSelectAddToPlaylist}
              onDelete={handleMultiSelectDelete}
            />
          </div>
        </div>
      )}

      {/* Toast notification - positioned where MultiSelect was */}
      {showToast && (
        <div 
          className="absolute left-4 z-50 flex justify-center pointer-events-none"
          style={{ 
            bottom: hasPlaybar ? '108px' : '24px',
            right: sidePanelOpen ? '290px' : '16px' // Adjust for side panel
          }}
        >
          <div className="pointer-events-auto">
            <Toast
              message={`Deleted ${deletedSongsBackup.size} song${deletedSongsBackup.size === 1 ? '' : 's'}!`}
              isVisible={showToast}
              onUndo={handleUndo}
              onDismiss={handleToastDismiss}
              type="success"
            />
          </div>
        </div>
      )}


      {/* Side Panel */}
      <SidePanel
        isOpen={sidePanelOpen}
        onClose={handleSidePanelClose}
        song={sidePanelSong}
        isWorkspace={true}
        isLiked={sidePanelSong ? likedSongs.has(sidePanelSong.id) : false}
        onLike={handleSidePanelLike}
        hasPlaybar={hasPlaybar}
        onTitleEdit={handleTitleEdit}
      />
    </div>
  );
};

export default Workspace;